import { Theme } from '@mui/material';

import { bm } from './bausteinMasse';

/**
 * Design-Test Stufe 2 (25.10.2026): Flaechen-Bausteine der Fall-Ansicht.
 *
 * Eine Quelle fuer Karte, Feldraster und Innenflaeche — die Tabs der
 * Fall-Ansicht erben sie, statt Rahmen, Radius und Innenabstand je Datei neu
 * zu schreiben (design-system.md R3). Nur Theme-Pfade, kein Hex.
 *
 * Abstaende: das Theme rechnet `spacing: 4`, also 2 = 8px, 4 = 16px,
 * 6 = 24px, 8 = 32px — die vier erlaubten Stufen.
 */

/**
 * Sektion als Karte: weiss, 1px Rand, 8px Ecken, 24px Innenabstand. Ecke,
 * Randfarbe und Innenabstand aus `theme.bausteinMasse.karte` — das Alt-Theme
 * der Beratungsstrecke setzt dort die Original-Werte (theme/streckeAlt).
 */
export const karteSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: (t: Theme) => bm(t).karte.rand,
  borderRadius: (t: Theme) => bm(t).karte.radius,
  px: (t: Theme) => bm(t).karte.px,
  py: (t: Theme) => bm(t).karte.py,
} as const;

/** Kopf einer Karte: Titel links, Aktionen rechts, 16px Abstand nach unten. */
export const karteKopfSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 4,
  mb: 4,
} as const;

/** Label/Wert-Felder in zwei Spalten. */
export const feldRasterSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: 6,
  rowGap: 4,
} as const;

/** Ruhige Innenflaeche (z. B. „Bevorstehend") auf einer Karte. */
export const innenflaecheSx = {
  bgcolor: 'background.subtle',
  borderRadius: '8px',
  p: 4,
} as const;

/** Klickbarer Text in Primary — ersetzt unterstrichene Links und Mini-Knoepfe. */
export const textLinkSx = {
  // Schrift per `variant="wertBetont"` am Element — an einer Typography
  // wuerde `sx.typography` die Primary-Farbe ueberstimmen.
  color: 'primary.main',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
} as const;

/**
 * Akzent-Linie (Stufe 2b, 25.10.2026): die farbigen Kachel-Flaechen des
 * Originals kommen in ihrer Original-Farbe zurueck, aber nur als duenne
 * 3-px-Linie am linken Rand, nicht als Flaeche. `farbe` ist ein Theme-Pfad.
 */
export const akzentLinieSx = (farbe: string) => ({
  borderLeft: '3px solid',
  borderLeftColor: farbe,
} as const);

/**
 * Aktionsleiste eines Dialogs (Design-Rollout 25.10.2026): rechtsbuendig,
 * 8px zwischen den Knoepfen, 24px Rand wie der Dialog-Inhalt. Fuer die
 * `ActionArea` der Hauskomponente `Modal` und fuer rohe `DialogActions`.
 * Reihenfolge der Knoepfe: Abbrechen (tertiary) links, die eine Hauptaktion
 * (secondary, Gold) rechts.
 */
export const dialogAktionenSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 2,
  px: 6,
  pb: 6,
} as const;
